import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import Button from './Button';

const CategoryCard = ({ title, description, image, isDarkMode }) => {
  return (
    <div 
      className={`relative w-full max-w-[703px] h-[100px] md:h-[131px] rounded-[70px] md:rounded-[70px] overflow-hidden group cursor-pointer`}
    >
      {/* Default Border */}
      <div className={`absolute inset-0 rounded-[70px] md:rounded-[70px] ${
        isDarkMode ? 'bg-gray-600' : 'bg-gray-200'
      } transition-opacity duration-300 group-hover:opacity-0`} />

      {/* Hover Gradient Border */}
      <div className={`absolute inset-0 rounded-[70px] md:rounded-[70px] bg-gradient-to-r ${
        isDarkMode 
          ? 'from-[#F49195] via-[#FCD239] to-[#000000]'
          : 'from-[#F49195] via-[#FCD239] to-[#FFFFFF]'
      } opacity-0 transition-opacity duration-300 group-hover:opacity-100`} />
      
      {/* Background Layer */}
      <div className={`absolute inset-[1px] rounded-[69px] md:rounded-[69px] ${
        isDarkMode ? 'bg-[#000000]' : 'bg-white'
      } overflow-hidden`}>
        {/* Content Container */}
        <div className="relative h-full flex">
          {/* Text Content - Adjusted padding and text sizes */}
          <div className="w-[70%] p-4 md:p-8 md:pl-10 flex flex-col justify-center text-left transition-transform duration-300 group-hover:translate-x-4">
            <h3 className={`text-[16px] md:text-[24px] font-lexend font-semibold mb-0.5 md:mb-2 ${
              isDarkMode ? 'text-white' : 'text-black'
            }`}>
              {title}
            </h3>
            <p className={`text-[12px] md:text-[16px] font-lexend font-light line-clamp-2 md:line-clamp-none ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {description}
            </p>
          </div>
          
          {/* Image Container - 30% */}
          <div className="w-[30%] h-full relative overflow-hidden">
            <div className="w-full h-full transform-gpu">
              <img 
                src={image} 
                alt={title}
                className="w-full h-full object-cover scale-[1.2] transition-transform duration-300 group-hover:scale-[1.3] origin-right"
              />
              {/* Four-point gradient overlay with specific opacities */}
              <div className={`absolute inset-0 ${
                isDarkMode 
                  ? 'bg-gradient-to-r from-[#000000] from-0% via-[#000000] via-20% via-[#000000]/50 via-40% to-transparent to-80%' 
                  : 'bg-gradient-to-r from-white from-0% via-white via-20% via-white/50 via-40% to-transparent to-80%'
              }`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RightForYouSection = () => {
  const { isDarkMode } = useTheme();

  const categories = [
    {
      title: "Data Analysts",
      description: "Turn raw data into meaningful insights while tackling exciting Quests.",
      // image: "/assets/Group 1863.png"
      image: "/assets/Rectangle 4513.png"
    },
    {
      title: "Data Scientists",
      description: "Showcase your skills in machine learning and predictive modeling.",
      image: "/assets/Rectangle 4514.png"
    },
    {
      title: "AI/ML Developers",
      description: "Build cutting-edge AI solutions and experiment with new technologies.",
      image: "/assets/Rectangle 4515.png"
    },
    {
      title: "Researchers",
      description: "Apply your academic expertise to solve practical, high-impact problems.",
      image: "/assets/Rectangle 4516.png"
    },
    {
      title: "Students and Beginners",
      description: "Start your journey in data science with beginner-friendly Quests.",
      image: "/assets/Rectangle 4517.png"
    },
    {
      title: "Organizations",
      description: "Launch Quests to crowdsource innovative solutions for your business.",
      image: "/assets/Rectangle 4518.png"
    }
  ];

  return (
    <section className={`relative pb-8 md:pb-16 pt-2 md:pt-4 px-3 md:px-4 font-lexend ${
      isDarkMode ? 'bg-[#000000]' : 'bg-white'
    }`}>
      <div className="max-w-[1300px] mx-auto">
        {/* Header - Adjusted margins and text sizes */}
        <div className="text-center mb-4 md:mb-8">
          <motion.h2 
            className={`text-[20px] md:text-[36px] font-lexend font-bold mb-2 md:mb-4 text-center md:text-left ${
              isDarkMode ? 'text-white' : 'text-black'
            }`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Is lunor.quest Right for You?
          </motion.h2>
          <motion.p 
            className={`text-[13px] md:text-[18px] w-2xl mx-auto font-lexend text-center md:text-left ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            lunor.quest is open to anyone passionate about data, AI, and solving real-world challenges
          </motion.p>
        </div>

        {/* Mobile CTA Section - Adjusted spacing */}
        <div className="lg:hidden mb-6 flex justify-center md:justify-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center md:text-left"
          >
            <h3 className={`text-[16px] md:text-[26px] font-lexend font-semibold mb-3 md:mb-6 ${
              isDarkMode ? 'text-white' : 'text-black'
            }`}>
              Find Quests That Matches Your Skills
            </h3>
            <Button
              text="Explore Quest"
              link="https://app.lunor.quest/"
              onClick={() => {}}
              className="gradient"
              size="small"
            />
          </motion.div>
        </div>

        {/* Main Content Container - Adjusted gap */}
        <div className="flex flex-col lg:flex-row gap-4 md:gap-8">
          {/* Cards Container */}
          <div className="flex-1">
            <div className="grid grid-cols-1 gap-3 md:gap-6">
              {categories.map((category, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <CategoryCard {...category} isDarkMode={isDarkMode} />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Desktop CTA Section - Right Side */}
          <div className="hidden lg:flex lg:w-[400px] items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center w-full sticky top-8"
            >
              <h3 className={`text-[26px] font-semibold mb-6 font-lexend ${
                isDarkMode ? 'text-white' : 'text-black'
              }`}>
                Find Quests That Matches Your Skills
              </h3>
              <Button
                text="Explore Quest"
                link="https://app.lunor.quest/"
                onClick={() => {}}
                className="gradient"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RightForYouSection; 